import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Typography } from "@mui/material";
import {
  DataGridTable,
  GridPillChip,
  listTableStickyBodyScrollSx,
  type DataGridTableRow,
} from "design-components";
import type { ReactNode } from "react";
import type { OperatorDetailLocationRow } from "../types";

type OperatorLocationsGridRow = OperatorDetailLocationRow & DataGridTableRow;

const HEADER_CLASSES = [
  "operator-detail-locations-table__th-location-name",
  "operator-detail-locations-table__th-address",
  "operator-detail-locations-table__th-city",
  "operator-detail-locations-table__th-state",
  "operator-detail-locations-table__th-status",
] as const;

export interface OperatorDetailLocationsTableProps {
  rows: OperatorDetailLocationRow[];
  selectedLocationIds: string[];
  onSelectedLocationIdsChange: (ids: string[]) => void;
  sortDescending: boolean;
  onLocationNameHeaderClick: () => void;
  paginationFooter?: ReactNode;
}

export function OperatorDetailLocationsTable(props: OperatorDetailLocationsTableProps) {
  const {
    rows,
    selectedLocationIds,
    onSelectedLocationIdsChange,
    sortDescending,
    onLocationNameHeaderClick,
    paginationFooter,
  } = props;

  const gridRows: OperatorLocationsGridRow[] = rows.map((row) => ({
    ...row,
    primaryCellLabel: row.locationName,
  }));

  return (
    <DataGridTable<OperatorLocationsGridRow>
      className="users-table operator-detail-locations-table operator-detail-locations-table--aviro"
      sx={{ width: "100%", maxWidth: "100%" }}
      tableContainerClassName="users-table__container"
      tableClassName="users-table__table"
      headRowClassName="users-table__head-row"
      selectionHeaderCellClassName="users-table__th-checkbox"
      selectionBodyCellClassName="users-table__cell-checkbox"
      headerDataCellClassName={(columnIndex) => HEADER_CLASSES[columnIndex]}
      rows={gridRows}
      dataColumnCount={5}
      selectionMode="multiple"
      selectedRowIds={selectedLocationIds}
      onSelectedRowIdsChange={onSelectedLocationIdsChange}
      trailingEmptyCell={false}
      getRowStateClass={() => "normal"}
      rowHover
      showSelectionColumn
      footer={paginationFooter}
      stickyHeader={Boolean(paginationFooter)}
      tableContainerSx={paginationFooter ? listTableStickyBodyScrollSx : undefined}
      renderHeaderCell={(columnIndex) => {
        if (columnIndex === 0) {
          return (
            <button type="button" className="users-table__sort-btn" onClick={onLocationNameHeaderClick}>
              <Typography className="aviro-grid-header__cell-text" component="span">
                Location name
              </Typography>
              {sortDescending ? (
                <KeyboardArrowDownIcon className="users-table__sort-icon" aria-hidden />
              ) : (
                <KeyboardArrowUpIcon className="users-table__sort-icon" aria-hidden />
              )}
            </button>
          );
        }
        if (columnIndex === 1) {
          return <Typography className="aviro-grid-header__cell-text">Address</Typography>;
        }
        if (columnIndex === 2) {
          return <Typography className="aviro-grid-header__cell-text">City</Typography>;
        }
        if (columnIndex === 3) {
          return <Typography className="aviro-grid-header__cell-text">State</Typography>;
        }
        return <Typography className="aviro-grid-header__cell-text">Scheduling Status</Typography>;
      }}
      renderBodyCell={(row, columnIndex) => {
        if (columnIndex === 0) {
          return (
            <Typography component="span" className="users-table__customer-name-link users-table__customer-name-text">
              {row.locationName}
            </Typography>
          );
        }
        if (columnIndex === 1) {
          return (
            <Typography className="aviro-grid-table-cell-text users-table__cell-ellipsis">{row.addressLine1}</Typography>
          );
        }
        if (columnIndex === 2) {
          return <Typography className="aviro-grid-table-cell-text">{row.city}</Typography>;
        }
        if (columnIndex === 3) {
          return <Typography className="aviro-grid-table-cell-text">{row.state}</Typography>;
        }
        return (
          <GridPillChip
            className="users-table__status-pill users-table__status-pill--customer-active operators-table__status-pill"
            pillVariant={row.status === "active" ? "breeze" : "critical"}
            label={row.status === "active" ? "ENABLED" : "DISABLED"}
            size="small"
          />
        );
      }}
    />
  );
}
